import React, { useState, useEffect } from 'react';
import {
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  Divider,
  Tooltip,
  Box,
  Typography,
  Chip,
  Badge,
  ListItem,
  CircularProgress,
  ListItemText,
  Button,
  alpha,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Slide,
  Grow,
  Snackbar,
  Alert
} from '@mui/material';
import {
  Logout as LogoutIcon,
  Settings as SettingsIcon,
  HelpOutline as HelpOutlineIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  ExitToApp as ExitToAppIcon,
  Refresh as RefreshIcon,
  FileDownload as ExportIcon,
  Notifications as NotificationsIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as SuccessIcon,
  Event as EventIcon,
  Payment as PaymentIcon,
  Inventory as InventoryIcon,
  Settings as SystemIcon
} from '@mui/icons-material';
import { useAuth } from '@contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { useDynamicTheme } from '../../contexts/DynamicThemeProvider';
import { useAvatar } from '../../contexts/AvatarContext';
import { notificationsService, Notification } from '@services/notificationsService';
import { formatDistanceToNow, format } from 'date-fns';
import { ro } from 'date-fns/locale';
import { formatRomaniaTime, toRomaniaTime } from '@utils/timezone';

interface HeaderRightProps {
  showNotifications?: boolean;
  showExport?: boolean;
  showHelp?: boolean;
  showRefresh?: boolean;
  onRefresh?: () => void;
  onExport?: () => void;
}

export default function HeaderRight({
  showNotifications = true,
  showExport = true,
  showHelp = true,
  showRefresh = true,
  onRefresh,
  onExport
}: HeaderRightProps) {
  const { user, signOut, isImpersonating, endImpersonation } = useAuth();
  const { toggleTheme, mode } = useDynamicTheme();
  const { avatarUrl } = useAvatar();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notificationsAnchorEl, setNotificationsAnchorEl] = useState<null | HTMLElement>(null);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [themeToastOpen, setThemeToastOpen] = useState(false);
  const [themeToastMessage, setThemeToastMessage] = useState('');
  const [helpMenuAnchor, setHelpMenuAnchor] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const notificationsOpen = Boolean(notificationsAnchorEl);
  const helpMenuOpen = Boolean(helpMenuAnchor);
  const navigate = useNavigate();
  const theme = useTheme();
  
  // State pentru notificari
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [notificationsError, setNotificationsError] = useState<string | null>(null);

  // Incarca notificarile
  useEffect(() => {
    if (user?.id) {
      loadNotifications();
      // Reincarca notificarile la fiecare 30 de secunde
      const interval = setInterval(loadNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [user?.id]);

  const loadNotifications = async () => {
    if (!user?.id) return;
    
    try {
      setLoadingNotifications(true);
      setNotificationsError(null);
      const response = await notificationsService.getNotifications({
        limit: 10,
        page: 1
      });
      
      setNotifications(response.notifications || []);
      const unread = (response.notifications || []).filter((n: Notification) => !n.read).length;
      setUnreadCount(unread);
    } catch (error: any) {
      // Nu logăm eroarea dacă backend-ul nu rulează
      if (!(error?.message?.includes('Failed to fetch') || error?.message?.includes('Network Error') || error?.code === 'ERR_NETWORK')) {
        console.error('❌ Eroare la incarcarea notificarilor:', error);
      }
      setNotifications([]);
      setUnreadCount(0);
      setNotificationsError('Notificarile nu au putut fi incarcate.');
    } finally {
      setLoadingNotifications(false);
    }
  };

  const handleAvatarClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationsClick = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationsAnchorEl(event.currentTarget);
  };

  const handleNotificationsClose = () => {
    setNotificationsAnchorEl(null);
  };

  const handleMarkAsRead = async (notificationId: string) => {
    try {
      await notificationsService.markAsRead(notificationId);
      await loadNotifications();
    } catch (error) {
      console.error('❌ Eroare la marcarea notificarii ca citita:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    if (!user?.id) return;
    try {
      await notificationsService.markAllAsRead();
      await loadNotifications();
    } catch (error) {
      console.error('Eroare la marcarea tuturor notificarilor ca citite:', error);
    }
  };

  const handleNotificationClick = async (notification: Notification) => {
    // Marcheaza ca citita
    if (!notification.read) {
      await handleMarkAsRead(notification.id);
    }
    
    // Navigheaza daca exista date in data
    if (notification.data?.relatedEntity) {
      const { type, id } = notification.data.relatedEntity;
      switch (type) {
        case 'appointment':
          navigate(`/appointments/${id}`);
          break;
        case 'payment':
          navigate(`/payments/${id}`);
          break;
        case 'patient':
          navigate(`/patients/${id}`);
          break;
        case 'client':
          navigate(`/clients/${id}`);
          break;
        case 'inventory':
          navigate(`/inventory/${id}`);
          break;
        default:
          break;
      }
    }
    
    handleNotificationsClose();
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'appointment_reminder':
        return <EventIcon fontSize="small" color="primary" />;
      case 'vaccination_due':
        return <WarningIcon fontSize="small" color="warning" />;
      case 'treatment_reminder':
        return <InfoIcon fontSize="small" color="info" />;
      case 'payment_confirmation':
        return <PaymentIcon fontSize="small" color="success" />;
      case 'emergency_alert':
        return <ErrorIcon fontSize="small" color="error" />;
      case 'general':
        return <SystemIcon fontSize="small" color="info" />;
      default:
        return <InfoIcon fontSize="small" />;
    }
  };

  const handleProfileClick = () => {
    setAnchorEl(null);
    navigate('/profile');
  };

  const handleSettingsClick = () => {
    setAnchorEl(null);
    // Navigheaza la setari diferite in functie de rol
    switch (user?.role) {
      case 'master_admin':
        navigate('/master-admin/settings');
        break;
      case 'clinic_admin':
        navigate('/clinic/settings');
        break;
      case 'shelter_admin':
        navigate('/shelter/settings');
        break;
      case 'veterinarian':
      case 'client':
      default:
        navigate('/settings'); // Setari generale pentru utilizatori obisnuiti
        break;
    }
  };

  const handleHelpClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(null);
    setHelpMenuAnchor(event.currentTarget);
  };

  const handleHelpMenuClose = () => {
    setHelpMenuAnchor(null);
  };

  const handleContactSupport = () => {
    setHelpMenuAnchor(null);
    // Navigheaza la pagina de contact sau deschide modal
    navigate('/contact');
  };

  const handleReportProblem = () => {
    setHelpMenuAnchor(null);
    // Navigheaza la pagina de raportare probleme
    navigate('/report-problem');
  };

  const handleUserGuide = () => {
    setHelpMenuAnchor(null);
    // Navigheaza la ghidul utilizatorului
    navigate('/help');
  };

  const handleThemeToggle = () => {
    setAnchorEl(null);
    toggleTheme();
    
    // Afiseaza toast feedback
    const newMode = mode === 'dark' ? 'light' : 'dark';
    setThemeToastMessage(newMode === 'dark' ? '🌙 Tema intunecata activata' : '☀️ Tema luminoasa activata');
    setThemeToastOpen(true);
  };

  const handleLogout = () => {
    setAnchorEl(null);
    setLogoutDialogOpen(true);
  };

  const handleConfirmLogout = async () => {
    setLogoutDialogOpen(false);
    await signOut();
  };

  const handleCancelLogout = () => {
    setLogoutDialogOpen(false);
  };

  const handleEndImpersonation = async () => {
    setAnchorEl(null);
    await endImpersonation();
  };

  // Returneaza un obiect Date valid din campurile posibile sau null
  const getNotificationDate = (n: any): Date | null => {
    const candidates = [
      n?.createdAt,
      n?.created_at,
      n?.sentAt,
      n?.sent_at,
      n?.readAt,
      n?.read_at,
      n?.updatedAt,
      n?.updated_at,
    ].filter(Boolean);
    
    for (const value of candidates) {
      try {
        // Incearca sa parsezi data si sa o convertesti la timezone Romania
        const romaniaDate = toRomaniaTime(value);
        if (!isNaN(romaniaDate.getTime())) {
          return romaniaDate;
        }
      } catch (error) {
        // Fallback la metoda veche
        const d = new Date(value);
        if (!isNaN(d.getTime())) return d;
      }
    }
    return null;
  };

  const handleRefresh = () => {
    if (onRefresh) {
      onRefresh();
    } else {
      window.location.reload();
    }
  };

  const handleExport = () => {
    if (onExport) {
      onExport();
    }
  };

  const getUserInitials = () => {
    if (!user) return 'U';
    const name = user.name || user.email || '';
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getRoleDisplayName = (role: string) => {
    const roleMap: Record<string, string> = {
      'master_admin': 'Master Admin',
      'admin': 'Administrator',
      'veterinarian': 'Veterinar',
      'clinic_admin': 'Admin Clinica',
      'client': 'Client',
      'user': 'Utilizator'
    };
    return roleMap[role] || role;
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      {/* Impersonation Banner */}
      {isImpersonating && (
        <Chip
          icon={<AdminIcon />}
          label={`Impersonating ${user?.name || 'User'}`}
          color="warning"
          variant="outlined"
          size="small"
          sx={{ mr: 1 }}
        />
      )}

      {/* Grupa 1: Actiuni secundare */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {/* Refresh Button */}
        {showRefresh && (
          <Tooltip title="Reimprospateaza">
            <IconButton
              onClick={handleRefresh}
              size="small"
              aria-label="Reimprospateaza pagina"
              sx={{ 
                color: 'text.secondary',
                '&:hover': {
                  color: 'primary.main',
                  backgroundColor: alpha(theme.palette.primary.main, 0.05)
                }
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        )}

        {/* Export Button */}
        {showExport && (
          <Tooltip title="Export date">
            <IconButton
              onClick={handleExport}
              size="small"
              aria-label="Export date"
              sx={{ 
                color: 'text.secondary',
                '&:hover': {
                  color: 'primary.main',
                  backgroundColor: alpha(theme.palette.primary.main, 0.05)
                }
              }}
            >
              <ExportIcon />
            </IconButton>
          </Tooltip>
        )}

        {/* Help Button */}
        {showHelp && (
          <Tooltip title="Ajutor">
            <IconButton
              onClick={() => navigate('/help')}
              size="small"
              aria-label="Ajutor si suport"
              sx={{ 
                color: 'text.secondary',
                '&:hover': {
                  color: 'primary.main',
                  backgroundColor: alpha(theme.palette.primary.main, 0.05)
                }
              }}
            >
              <HelpOutlineIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Divider vertical */}
      <Divider 
        orientation="vertical" 
        flexItem 
        sx={{ 
          mx: 1,
          height: 24,
          alignSelf: 'center'
        }} 
      />

      {/* Grupa 2: Utilizator */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {/* Notifications Button */}
        {showNotifications && (
          <Tooltip title="Notificari">
            <IconButton
              onClick={handleNotificationsClick}
              size="small"
              aria-label="Notificari"
              sx={{ 
                color: unreadCount > 0 ? 'primary.main' : 'text.secondary',
                '&:hover': {
                  color: 'primary.main',
                  backgroundColor: alpha(theme.palette.primary.main, 0.05)
                }
              }}
            >
              <Badge badgeContent={unreadCount} color="error" max={99}>
                <NotificationsIcon />
              </Badge>
            </IconButton>
          </Tooltip>
        )}

        {/* User Avatar */}
        <Tooltip 
          title={
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {user?.firstName && user?.lastName 
                  ? `${user.firstName} ${user.lastName}` 
                  : user?.name || 'Utilizator'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {getRoleDisplayName(user?.role || '')}
              </Typography>
            </Box>
          }
          placement="bottom-end"
        >
          <IconButton
            onClick={handleAvatarClick}
            size="small"
            aria-label="Deschide meniul utilizator"
            sx={{ 
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.05)
              }
            }}
          >
            <Avatar
              src={avatarUrl || undefined}
              sx={{
                width: 32,
                height: 32,
                bgcolor: isImpersonating ? 'warning.main' : 'primary.main',
                fontSize: '0.875rem',
                fontWeight: 600
              }}
            >
              {!avatarUrl && getUserInitials()}
            </Avatar>
          </IconButton>
        </Tooltip>
      </Box>

      {/* User Menu */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          sx: {
            minWidth: 260,
            maxWidth: 300,
            mt: 1,
            borderRadius: 3,
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            border: `1px solid ${theme.palette.divider}`
          }
        }}
        TransitionComponent={Grow}
        transitionDuration={200}
      >
        {/* User Info Header */}
        <Box sx={{ 
          px: 2, 
          py: 1.5, 
          borderBottom: 1, 
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          minHeight: 64
        }}>
                  <Avatar
                    src={avatarUrl || undefined}
                    sx={{
                      width: 40,
                      height: 40,
                      bgcolor: isImpersonating ? 'warning.main' : 'primary.main',
                      fontSize: '0.875rem',
                      fontWeight: 600
                    }}
                  >
                    {!avatarUrl && getUserInitials()}
                  </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography 
              variant="body1" 
              sx={{ 
                fontWeight: 600, 
                fontSize: '16px',
                lineHeight: 1.2,
                mb: 0.5
              }}
            >
              {user?.firstName && user?.lastName 
                ? `${user.firstName} ${user.lastName}` 
                : user?.name || 'Utilizator'}
            </Typography>
            
            {/* Role Badge */}
            <Box sx={{ mb: 0.5 }}>
              <Chip
                label={getRoleDisplayName(user?.role || 'user')}
                size="small"
                color="primary"
                variant="outlined"
                sx={{
                  fontSize: '11px',
                  height: 20,
                  fontWeight: 500
                }}
              />
            </Box>
            
            {user?.email && (
              <Typography 
                variant="caption" 
                color="text.secondary"
                sx={{ 
                  fontSize: '12px',
                  display: 'block',
                  lineHeight: 1.2
                }}
              >
                {user.email}
              </Typography>
            )}
            
            {/* Member since info */}
            {user?.createdAt && (
              <Typography 
                variant="caption" 
                color="text.disabled"
                sx={{ 
                  fontSize: '10px',
                  display: 'block',
                  mt: 0.25
                }}
              >
                Membru din: {user.createdAt ? format(new Date(user.createdAt), 'MMMM yyyy', { locale: ro }) : 'Data indisponibila'}
              </Typography>
            )}
            
            {/* Clinic name - SUB membru din */}
            {user?.clinic?.name && (
              <Typography 
                variant="caption" 
                color="primary.main"
                sx={{ 
                  fontSize: '10px',
                  display: 'block',
                  fontWeight: 500,
                  mt: 0.25
                }}
              >
                {user.clinic.name}
              </Typography>
            )}
          </Box>
        </Box>

        {/* 🧍 Contul meu */}
        <Box sx={{ px: 2, py: 1 }}>
          <Typography 
            variant="caption" 
            color="text.secondary" 
            sx={{ 
              fontSize: '12px', 
              fontWeight: 600, 
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              opacity: 0.8
            }}
          >
            🧍 Contul meu
          </Typography>
        </Box>

        {/* Profile */}
        <MenuItem 
          onClick={handleProfileClick}
          sx={{
            height: 40,
            px: 2,
            py: 0.75,
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.05)
            }
          }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <PersonIcon sx={{ fontSize: 18, opacity: 0.8 }} />
          </ListItemIcon>
          <Typography variant="body2" sx={{ fontSize: '14px', fontWeight: 500 }}>
            Profil
          </Typography>
        </MenuItem>

        {/* Settings */}
        <MenuItem 
          onClick={handleSettingsClick}
          sx={{
            height: 40,
            px: 2,
            py: 0.75,
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.05)
            }
          }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <SettingsIcon sx={{ fontSize: 18, opacity: 0.8 }} />
          </ListItemIcon>
          <Typography variant="body2" sx={{ fontSize: '14px', fontWeight: 500 }}>
            {user?.role === 'master_admin' ? 'Setari sistem' :
             user?.role === 'clinic_admin' ? 'Setari clinica' : 'Setari'}
          </Typography>
        </MenuItem>

        <Divider sx={{ my: 0.5 }} />

        {/* ⚙️ Preferinte */}
        <Box sx={{ px: 2, py: 1 }}>
          <Typography 
            variant="caption" 
            color="text.secondary" 
            sx={{ 
              fontSize: '12px', 
              fontWeight: 600, 
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              opacity: 0.8
            }}
          >
            ⚙️ Preferinte
          </Typography>
        </Box>

        {/* Theme Toggle with Switch */}
        <MenuItem 
          sx={{
            height: 40,
            px: 2,
            py: 0.75,
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.05)
            }
          }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            {mode === 'dark' ? (
              <LightModeIcon sx={{ fontSize: 18, opacity: 0.8 }} />
            ) : (
              <DarkModeIcon sx={{ fontSize: 18, opacity: 0.8 }} />
            )}
          </ListItemIcon>
          <Typography variant="body2" sx={{ fontSize: '14px', fontWeight: 500, flex: 1 }}>
            {mode === 'dark' ? 'Tema luminoasa' : 'Tema intunecata'}
          </Typography>
          <Switch 
            checked={mode === 'dark'} 
            onChange={(e) => {
              e.stopPropagation();
              handleThemeToggle();
            }}
            size="small"
            sx={{ ml: 1 }}
          />
        </MenuItem>

        {/* Help */}
        <MenuItem 
          onClick={handleHelpClick}
          sx={{
            height: 40,
            px: 2,
            py: 0.75,
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.05)
            }
          }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <HelpOutlineIcon sx={{ fontSize: 18, opacity: 0.8 }} />
          </ListItemIcon>
          <Typography variant="body2" sx={{ fontSize: '14px', fontWeight: 500, flex: 1 }}>
            Ajutor
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '12px', opacity: 0.6 }}>
            ›
          </Typography>
        </MenuItem>

        <Divider sx={{ my: 0.5 }} />

        {/* End Impersonation (if impersonating) */}
        {isImpersonating && (
          <MenuItem 
            onClick={handleEndImpersonation}
            sx={{
              height: 40,
              px: 2,
              py: 0.75,
              color: 'warning.main',
              '&:hover': {
                backgroundColor: mode === 'dark' 
                  ? 'rgba(255,255,255,0.06)' 
                  : 'rgba(0,0,0,0.04)'
              }
            }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <ExitToAppIcon sx={{ fontSize: 18, opacity: 0.8, color: 'warning.main' }} />
            </ListItemIcon>
            <Typography variant="body2" sx={{ fontSize: '14px', fontWeight: 500 }}>
              Termina impersonarea
            </Typography>
          </MenuItem>
        )}

        {/* Logout */}
        {!isImpersonating && (
          <MenuItem
            onClick={handleLogout}
            aria-label="Deconecteaza-te"
            sx={{
              height: 40,
              px: 2,
              py: 0.75,
              color: 'error.main',
              '&:hover': {
                backgroundColor: alpha(theme.palette.error.main, 0.05)
              }
            }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <LogoutIcon sx={{ fontSize: 18, opacity: 0.8, color: 'error.main' }} />
            </ListItemIcon>
            <Typography variant="body2" sx={{ fontSize: '14px', fontWeight: 500 }}>
              Deconecteaza-te
            </Typography>
          </MenuItem>
        )}
      </Menu>

      {/* Help Menu */}
      <Menu
        anchorEl={helpMenuAnchor}
        open={helpMenuOpen}
        onClose={handleHelpMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'left', vertical: 'top' }}
        PaperProps={{
          sx: {
            minWidth: 180,
            borderRadius: 2,
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            border: `1px solid ${theme.palette.divider}`
          }
        }}
        TransitionComponent={Grow}
        transitionDuration={150}
      >
        <MenuItem onClick={handleUserGuide}>
          <ListItemIcon sx={{ minWidth: 36 }}>
            <HelpOutlineIcon sx={{ fontSize: 18 }} />
          </ListItemIcon>
          <ListItemText 
            primary="Ghid utilizator"
            primaryTypographyProps={{ fontSize: '14px' }}
          />
        </MenuItem>
        <MenuItem onClick={handleContactSupport}>
          <ListItemIcon sx={{ minWidth: 36 }}>
            <InfoIcon sx={{ fontSize: 18 }} />
          </ListItemIcon>
          <ListItemText 
            primary="Contacteaza suportul"
            primaryTypographyProps={{ fontSize: '14px' }}
          />
        </MenuItem>
        <MenuItem onClick={handleReportProblem}>
          <ListItemIcon sx={{ minWidth: 36 }}>
            <ErrorIcon sx={{ fontSize: 18 }} />
          </ListItemIcon>
          <ListItemText 
            primary="Raporteaza problema"
            primaryTypographyProps={{ fontSize: '14px' }}
          />
        </MenuItem>
      </Menu>

      {/* Notifications Menu */}
      <Menu
        anchorEl={notificationsAnchorEl}
        open={notificationsOpen}
        onClose={handleNotificationsClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          sx: {
            width: 340,
            maxHeight: 450,
            mt: 1
          }
        }}
      >
        {/* Header */}
        <Box sx={{ 
          px: 2, 
          py: 1.5, 
          borderBottom: 1, 
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Notificari {unreadCount > 0 && `(${unreadCount})`}
          </Typography>
          {notifications.length > 0 && unreadCount > 0 && (
            <Button
              size="small"
              onClick={handleMarkAllAsRead}
              sx={{ textTransform: 'none' }}
            >
              Marcheaza toate ca citite
            </Button>
          )}
        </Box>

        {/* Loading state */}
        {loadingNotifications && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <CircularProgress size={32} />
          </Box>
        )}

        {!loadingNotifications && notificationsError && (
          <Box sx={{ p: 2 }}>
            <Alert severity="error">{notificationsError}</Alert>
          </Box>
        )}

        {/* Notifications list */}
        {!loadingNotifications && !notificationsError && notifications.length > 0 && (
          <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
            {notifications.map((notification) => (
              <MenuItem
                key={notification.id}
                onClick={() => handleNotificationClick(notification)}
                sx={{
                  py: 1.5,
                  px: 2,
                  borderBottom: 1,
                  borderColor: 'divider',
                  backgroundColor: notification.read ? 'transparent' : 'action.hover',
                  '&:hover': {
                    backgroundColor: notification.read ? 'action.hover' : 'action.selected'
                  },
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5
                }}
              >
                <ListItemIcon sx={{ minWidth: 'auto', mt: 0.5 }}>
                  {getNotificationIcon(notification.type)}
                </ListItemIcon>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: notification.read ? 400 : 600,
                      mb: 0.5
                    }}
                  >
                    {notification.title}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    color="text.secondary"
                    sx={{ 
                      mb: 0.5,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical'
                    }}
                  >
                    {notification.message}
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    {(() => {
                      const date = getNotificationDate(notification);
                      return date
                        ? formatDistanceToNow(date, { addSuffix: true, locale: ro })
                        : '';
                    })()}
                  </Typography>
                </Box>
                {!notification.read && (
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: 'primary.main',
                      flexShrink: 0,
                      mt: 1
                    }}
                  />
                )}
              </MenuItem>
            ))}
          </Box>
        )}

        {/* Empty state */}
        {!loadingNotifications && !notificationsError && notifications.length === 0 && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <NotificationsIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              Nu ai notificari noi
            </Typography>
          </Box>
        )}

        {/* Footer */}
        {!loadingNotifications && !notificationsError && notifications.length > 0 && (
          <Box sx={{ p: 1.5, borderTop: 1, borderColor: 'divider', textAlign: 'center' }}>
            <Button
              size="small"
              onClick={() => {
                navigate(user?.role === 'client' ? '/my-notifications' : '/notifications');
                handleNotificationsClose();
              }}
              sx={{ textTransform: 'none' }}
            >
              Vezi toate notificarile
            </Button>
          </Box>
        )}
      </Menu>

      {/* Logout Confirmation Dialog */}
      <Dialog
        open={logoutDialogOpen}
        onClose={handleCancelLogout}
        aria-labelledby="logout-dialog-title"
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
          }
        }}
      >
        <DialogTitle id="logout-dialog-title" sx={{ pb: 1 }}>
          Confirma deconectarea
        </DialogTitle>
        <DialogContent sx={{ pb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Esti sigur ca vrei sa te deconectezi din aplicatie?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={handleCancelLogout}
            variant="outlined"
            size="small"
            sx={{ borderRadius: 2 }}
          >
            Anuleaza
          </Button>
          <Button
            onClick={handleConfirmLogout}
            variant="contained"
            color="error"
            size="small"
            sx={{ borderRadius: 2 }}
          >
            Deconecteaza-te
          </Button>
        </DialogActions>
      </Dialog>

      {/* Theme Toast Feedback */}
      <Snackbar
        open={themeToastOpen}
        autoHideDuration={1500}
        onClose={() => setThemeToastOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ mt: 8 }}
      >
        <Alert 
          onClose={() => setThemeToastOpen(false)} 
          severity="info"
          variant="filled"
          sx={{ 
            borderRadius: 2,
            backgroundColor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.700',
            '& .MuiAlert-message': {
              fontSize: '14px',
              fontWeight: 500
            }
          }}
        >
          {themeToastMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
